import React, { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import { HoldingWithInstrumentAndAccount } from "@/types";
import moment from "moment-timezone";

interface CurrencyTrendData {
  period: ReportingPeriod;
  currencyTotals: Record<string, number>;
  date: string;
}

interface CurrencyTrendChartProps {
  periodHoldingsData: { period: ReportingPeriod; holdings: HoldingWithInstrumentAndAccount[] }[];
  isLoading: boolean;
}

export const CurrencyTrendChart: React.FC<CurrencyTrendChartProps> = ({
  periodHoldingsData,
  isLoading,
}) => {
  // Process data to calculate currency totals for each period
  const trendData = useMemo((): CurrencyTrendData[] => {
    return periodHoldingsData
      .map(({ period, holdings }) => {
        const currencyTotals = holdings.reduce((totals, holding) => {
          const currency = holding.currency || "USD";
          if (!totals[currency]) {
            totals[currency] = 0;
          }
          totals[currency] += holding.baseAmount;
          return totals;
        }, {} as Record<string, number>);

        return {
          period,
          currencyTotals,
          date: moment(period.period).format("MMM YYYY"),
        };
      })
      .sort((a, b) => new Date(a.period.period).getTime() - new Date(b.period.period).getTime());
  }, [periodHoldingsData]);

  // Get all unique currencies across all periods
  const allCurrencies = useMemo(() => {
    const currencies = new Set<string>();
    trendData.forEach(data => {
      Object.keys(data.currencyTotals).forEach(currency => currencies.add(currency));
    });
    return Array.from(currencies).sort();
  }, [trendData]);

  // Calculate changes between periods for each currency
  const currencyChanges = useMemo(() => {
    const changes: Record<string, { absolute: number; percentage: number; trend: 'up' | 'down' | 'flat' }[]> = {};
    
    allCurrencies.forEach(currency => {
      changes[currency] = [];
      
      for (let i = 1; i < trendData.length; i++) {
        const prevAmount = trendData[i - 1].currencyTotals[currency] || 0;
        const currentAmount = trendData[i].currencyTotals[currency] || 0;
        const absolute = currentAmount - prevAmount;
        const percentage = prevAmount !== 0 ? (absolute / Math.abs(prevAmount)) * 100 : 
                          (currentAmount > 0 ? 100 : 0); // Handle zero to non-zero transitions
        
        let trend: 'up' | 'down' | 'flat' = 'flat';
        if (absolute > 0) trend = 'up';
        else if (absolute < 0) trend = 'down';
        
        changes[currency].push({ absolute, percentage, trend });
      }
    });
    
    return changes;
  }, [trendData, allCurrencies]);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Currency Trends</CardTitle>
          <CardDescription>Multi-period currency value changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (trendData.length < 2) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Currency Trends</CardTitle>
          <CardDescription>Multi-period currency value changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Select at least 2 periods to view currency trends
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Currency Trends</CardTitle>
        <CardDescription>
          Currency values across {trendData.length} reporting periods
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Period Headers */}
          <div className="overflow-x-auto">
            <div className="grid grid-cols-1 gap-4 min-w-max">
              <div className="flex gap-4 text-sm font-medium text-muted-foreground">
                <div className="w-20">Currency</div>
                {trendData.map((data, index) => (
                  <div key={data.period.reportingPeriodId} className="w-32 text-center">
                    {data.date}
                    {index > 0 && (
                      <div className="text-xs mt-1">vs Previous</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Currency Rows */}
              {allCurrencies.map(currency => (
                <div key={currency} className="flex gap-4 items-center py-3 border-t border-border/50">
                  <div className="w-20">
                    <Badge variant="outline" className="font-semibold">
                      {currency}
                    </Badge>
                  </div>
                  
                  {trendData.map((data, periodIndex) => {
                    const amount = data.currencyTotals[currency] || 0;
                    const change = periodIndex > 0 ? currencyChanges[currency][periodIndex - 1] : null;
                    
                                         return (
                       <div key={data.period.reportingPeriodId} className="w-32 text-center">
                         <div className="font-semibold text-sm">
                           {formatCurrency(amount, currency)}
                         </div>
                        
                        {change && (
                          <div className="mt-1 space-y-1">
                            <div className={`flex items-center justify-center gap-1 text-xs ${
                              change.trend === 'up' ? 'text-green-600' :
                              change.trend === 'down' ? 'text-red-600' : 'text-muted-foreground'
                            }`}>
                              {change.trend === 'up' && <TrendingUp className="h-3 w-3" />}
                              {change.trend === 'down' && <TrendingDown className="h-3 w-3" />}
                              {change.trend === 'flat' && <Minus className="h-3 w-3" />}
                              <span>{formatCurrency(Math.abs(change.absolute), currency)}</span>
                            </div>
                            <div className={`text-xs ${
                              change.trend === 'up' ? 'text-green-600' :
                              change.trend === 'down' ? 'text-red-600' : 'text-muted-foreground'
                            }`}>
                              {formatPercentage(change.percentage)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Summary Stats */}
          {trendData.length > 1 && (
            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-medium mb-3">Overall Period-to-Period Changes</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {allCurrencies.map(currency => {
                  const changes = currencyChanges[currency];
                  const totalChange = changes.reduce((sum, change) => sum + change.absolute, 0);
                  const firstAmount = trendData[0].currencyTotals[currency] || 0;
                  const lastAmount = trendData[trendData.length - 1].currencyTotals[currency] || 0;
                  const overallPercentage = firstAmount !== 0 ? ((lastAmount - firstAmount) / Math.abs(firstAmount)) * 100 : 0;
                  
                  return (
                    <div key={currency} className="p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="text-xs">
                          {currency}
                        </Badge>
                        <div className={`flex items-center gap-1 text-xs ${
                          overallPercentage > 0 ? 'text-green-600' :
                          overallPercentage < 0 ? 'text-red-600' : 'text-muted-foreground'
                        }`}>
                          {overallPercentage > 0 && <TrendingUp className="h-3 w-3" />}
                          {overallPercentage < 0 && <TrendingDown className="h-3 w-3" />}
                          {overallPercentage === 0 && <Minus className="h-3 w-3" />}
                          {formatPercentage(overallPercentage)}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatCurrency(firstAmount, currency)} → {formatCurrency(lastAmount, currency)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}; 